import React, { useEffect, useState } from "react";
import { HashRouter as Router } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Box,
  Avatar,
  Paper,
  Fade,
} from "@mui/material";

import {
  WebOutlined,
  PhoneAndroid,
  Code,
  Build,
  AccessAlarm,
  CheckCircle,
  Settings,
} from "@mui/icons-material";
import Navbar from "./Navbar";
import Language from "./Language";
import header from "../Images/header.jpg";
// import logo192 from "../ImageProject/logo192.png";
// import logo192_1 from "../ImageProject/logo192 (1).png";
// import logo192_2 from "../ImageProject/logo192 (2).png";
// import adaptiveIcon from "../ImageProject/adaptive-icon.png";
// import primarySchool from "../ImageProject/PrimaryShool.png";
// import logo from "../ImageProject/logo.jpg";
// import logo512 from "../ImageProject/logo512.png";
import "../App.css";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={500}>
          <Box sx={{ py: 3 }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

function Home() {
  const { t } = useTranslation();

  return (
    <Router>
      <div>
        <Navbar />
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: "auto", md: "80vh" }, // Changed to auto for mobile
            minHeight: { xs: "100vh", md: "auto" }, // Ensure full viewport height on mobile
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Left side - Image */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              height: { xs: "50vh", md: "100%" }, // Fixed height for mobile
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={header}
              alt={t("Header Image")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* Right side - Welcome Text */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              height: { xs: "auto", md: "100%" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: { xs: 2, md: 6 }, // Reduced padding on mobile
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "90%", md: "600px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 1.5, md: 3 }, // Consistent gap between elements
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "1.8rem", md: "3.5rem" }, // Adjusted for mobile
                  fontWeight: 900,
                  color: "#FFFFFF",
                  marginRight: { xs: 4, md: "auto" },
                  width: "100%",
                  lineHeight: 1.2,
                }}
              >
                {t("Welcome to TechTopia")}
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "1rem", md: "1.5rem" }, // Adjusted for mobile
                  color: "#FF00FF",
                  width: "100%",
                  marginRight: { xs: 4, md: "auto" },
                  lineHeight: 1.3,
                  fontWeight: 700,
                }}
              >
                {t("Innovative Technology Solutions for Your Business")}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "0.8rem", md: "1.1rem" }, // Adjusted for mobile
                  color: "#bbbbbb",
                  width: "100%",
                  marginRight: { xs: 4, md: "auto" },
                  lineHeight: 1.6,
                  opacity: 0.9,
                }}
              >
                {t(
                  "We help businesses transform and grow through cutting-edge technology solutions and expert consulting services."
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Page />
      </div>
    </Router>
  );
}

const Page = () => {
  const [tabValue] = useState(0);
  const { t, i18n } = useTranslation();
  const direction = ["ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ku"].includes(i18n.language) ? "right" : "left";

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.substring(1);
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, md: 8 },
          direction,
          textAlign,
        }}
      >
        {/* Company Tabs Section */}
        <section id="AboutUs" style={{ padding: "80px 0" }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
              direction,
              textAlign,
            }}
          >
            <Typography
              variant="h3"
              gutterBottom
              fontWeight={600}
              color="primary"
              align="center"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                mb: { xs: 4, md: 6 },
              }}
            >
              {t("Our Company")}
            </Typography>

            <TabPanel value={tabValue} index={0}>
              <Grid
                container
                spacing={{
                  xs: 2,
                  md: 4,
                }}
              >
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.5rem" },
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    {t("Who We Are")}
                  </Typography>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      lineHeight: 1.8,
                    }}
                  >
                    {t(
                      "TechTopia is a leading technology company dedicated to delivering innovative solutions that drive business growth. With over 5 years of experience, we've established ourselves as a trusted partner for businesses seeking digital transformation."
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1.1rem" },
                      lineHeight: 1.8,
                    }}
                  >
                    {t(
                      "Our team of experts combines technical excellence with creative thinking to deliver solutions that exceed expectations and create lasting value for our clients."
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2, md: 3 },
                        direction,
                        textAlign,
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontSize: { xs: "1rem", md: "1.25rem" },
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        {t("Our Mission")}
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                      >
                        {t(
                          "To empower businesses through innovative technology solutions that drive growth and success in the digital age."
                        )}
                      </Typography>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontSize: { xs: "1rem", md: "1.25rem" },
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        {t("Our Vision")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                      >
                        {t(
                          "To be the leading technology partner for businesses seeking digital transformation and innovation."
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          </Paper>
        </section>

        {/* Services Section */}
        <section
          id="services"
          style={{ padding: "80px 0", direction, textAlign }}
        >
          <Box
            sx={{
              maxWidth: 1200,
              mx: "auto",
              px: { xs: 2, sm: 4 },
              direction,
              textAlign,
            }}
          >
            <Typography
              variant="h3"
              gutterBottom
              fontWeight={600}
              color="primary"
              align="center"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                mb: { xs: 4, md: 6 },
              }}
            >
              {t("Our Services")}
            </Typography>
            <Grid
              container
              spacing={{
                xs: 2,
                md: 4,
              }}
              justifyContent="center"
            >
              {[
                {
                  title: t("Website Development"),
                  icon: (
                    <WebOutlined sx={{ fontSize: 40, color: "primary.main" }} />
                  ),
                  details: [
                    t(
                      "Custom-designed websites that reflect your brand identity."
                    ),
                    t(
                      "Responsive and user-friendly designs for optimal user experience."
                    ),
                    t(
                      "Integration with CMS, e-commerce platforms, and third-party tools."
                    ),
                  ],
                },
                {
                  title: t("Mobile App Development"),
                  icon: (
                    <PhoneAndroid
                      sx={{ fontSize: 40, color: "primary.main" }}
                    />
                  ),
                  details: [
                    t("Cross-platform applications for iOS and Android."),
                    t(
                      "Scalable and secure apps with user-friendly interfaces."
                    ),
                    t(
                      "Expertise in integrating advanced features like geolocation, push notifications, and in-app payments."
                    ),
                  ],
                },
                {
                  title: t("Custom Software Solutions"),
                  icon: <Code sx={{ fontSize: 40, color: "primary.main" }} />,
                  details: [
                    t(
                      "Bespoke software systems to address unique operational challenges."
                    ),
                    t(
                      "Integration with existing systems for seamless functionality."
                    ),
                    t("Scalable solutions to grow with your business."),
                  ],
                },
                {
                  title: t("Maintenance and Support"),
                  icon: <Build sx={{ fontSize: 40, color: "primary.main" }} />,
                  details: [
                    t("Ongoing technical support and updates."),
                    t("Performance monitoring and optimization."),
                    t("Security audits and enhancements."),
                  ],
                },
              ].map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-10px)",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2, md: 3 },
                        direction,
                        textAlign,
                      }}
                    >
                      <Box sx={{ mb: { xs: 1, md: 2 }, textAlign: "center" }}>
                        {service.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        gutterBottom
                        align="center"
                        sx={{
                          fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" },
                          mb: { xs: 1, md: 2 },
                        }}
                      >
                        {service.title}
                      </Typography>
                      <List
                        sx={{
                          listStyleType: "none",
                          pl: direction === "rtl" ? 0 : 0,
                          pr: direction === "rtl" ? 0 : 0,
                          textAlign,
                        }}
                      >
                        {service.details.map((detail, idx) => (
                          <ListItem
                            key={idx}
                            disableGutters
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexDirection:
                                direction === "rtl" ? "row-reverse" : "row",
                              p: 0,
                              mb: 1,
                            }}
                          >
                            {/* <CheckCircle
                              sx={{
                                fontSize: { xs: 18, md: 22 },
                                color: "primary.main",
                                mr:
                                  direction === "rtl" ? 0 : { xs: 1, md: 1.5 },
                                ml:
                                  direction === "rtl" ? { xs: 1, md: 1.5 } : 0,
                                flexShrink: 0,
                              }}
                            /> */}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                color: "text.secondary",
                                textAlign: textAlign,
                                flexGrow: 1,
                              }}
                            >
                              {detail}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </section>

        {/* Our Approach Section */}
        <section
          id="OurApproach"
          style={{ padding: "80px 0", direction, textAlign }}
        >
          <Container maxWidth="lg" sx={{ direction, textAlign }}>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={600}
              color="primary"
              align="center"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                mb: { xs: 4, md: 6 },
              }}
            >
              {t("Our Approach")}
            </Typography>
            <Grid
              container
              spacing={{
                xs: 2,
                md: 3,
              }}
            >
              {[
                {
                  step: t("1. Understanding Your Needs"),
                  description: t(
                    "We begin by thoroughly understanding your business objectives, challenges, and goals to design a solution that perfectly aligns with your vision."
                  ),
                },
                {
                  step: t("2. Design and Development"),
                  description: t(
                    "Our team works diligently to craft intuitive designs and robust systems, ensuring they meet the highest standards of functionality and usability."
                  ),
                },
                {
                  step: t("3. Testing and Deployment"),
                  description: t(
                    "Rigorous testing ensures that your solution is free of errors and performs optimally. Once approved, we deploy it to your chosen platforms seamlessly."
                  ),
                },
                {
                  step: t("4. Post-Deployment Support"),
                  description: t(
                    "We provide ongoing support and maintenance to ensure long-term success, making necessary updates and optimizations as your needs evolve."
                  ),
                },
              ].map((item, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box
                    sx={{
                      p: { xs: 2, md: 2 },
                      borderLeft: direction === "rtl" ? "none" : "4px solid",
                      borderRight: direction === "rtl" ? "4px solid" : "none",
                      borderColor: "primary.main",
                      bgcolor: "background.paper",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                        transform:
                          direction === "rtl"
                            ? "translateX(-10px)"
                            : "translateX(10px)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="primary"
                      gutterBottom
                      sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                    >
                      {item.step}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </section>

        {/* Why Choose Us Section */}

        <section
          id="WhyChooseUs"
          style={{ padding: "80px 0", direction, textAlign }}
        >
          <Typography
            variant="h3"
            gutterBottom
            fontWeight={600}
            color="primary"
            align="center"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              mb: { xs: 4, md: 6 },
            }}
          >
            {t("Why Choose Us?")}
          </Typography>
          <Grid
            container
            spacing={{
              xs: 2,
              md: 3,
            }}
            justifyContent="center"
            sx={{
              direction: direction,
              textAlign: textAlign,
            }}
          >
            {[
              {
                title: t("Expertise"),
                description: t(
                  "Proven track record in delivering high-quality software solutions across various industries."
                ),
                icon: <Settings sx={{ fontSize: 35, color: "white" }} />,
              },
              {
                title: t("Customization"),
                description: t(
                  "Tailored solutions that address your unique requirements."
                ),
                icon: <Settings sx={{ fontSize: 35, color: "white" }} />,
              },
              {
                title: t("Reliability"),
                description: t(
                  "Timely delivery and consistent post-launch support."
                ),
                icon: <AccessAlarm sx={{ fontSize: 35, color: "white" }} />,
              },
              {
                title: t("Innovation"),
                description: t(
                  "Use of the latest technologies to ensure modern and scalable solutions."
                ),
                icon: <Code sx={{ fontSize: 35, color: "white" }} />,
              },
              {
                title: t("Client-Centric Approach"),
                description: t(
                  "A collaborative process that prioritizes your input at every stage."
                ),
                icon: <CheckCircle sx={{ fontSize: 35, color: "white" }} />,
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 2, md: 3 },
                      direction,
                      textAlign,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: { xs: 1, md: 2 },
                        flexDirection:
                          direction === "rtl" ? "row-reverse" : "row",
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          mr: direction === "rtl" ? 0 : { xs: 1.5, md: 2 },
                          ml: direction === "rtl" ? { xs: 1.5, md: 2 } : 0,
                          width: { xs: 50, md: 60 },
                          height: { xs: 50, md: 60 },
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: "1rem", md: "1.25rem" },
                          flexGrow: 1,
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        pl: direction === "rtl" ? 0 : { xs: 6.5, md: 8 },
                        pr: direction === "rtl" ? { xs: 6.5, md: 8 } : 0,
                        fontSize: { xs: "0.9rem", md: "1rem" },
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </section>

        {/* Proposed Solution Section */}
        <section
          id="ProposedSolution"
          style={{
            padding: "80px 0",
            direction: direction,
            textAlign: textAlign,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
              direction: direction,
              textAlign: textAlign,
            }}
          >
            <Typography
              variant="h3"
              gutterBottom
              fontWeight={600}
              color="primary"
              align="center"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                mb: { xs: 4, md: 4 },
              }}
            >
              {t("Proposed Solution")}
            </Typography>
            <Box
              sx={{
                maxWidth: 800,
                mx: "auto",
                direction: direction,
                textAlign: textAlign,
              }}
            >
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: { xs: "0.9rem", md: "1.1rem" },
                  mb: { xs: 3, md: 4 },
                  textAlign: textAlign,
                }}
              >
                {t("Based on your requirements, we propose to:")}
              </Typography>
              <List
                sx={{
                  direction: direction,
                  textAlign: textAlign,
                  pl: direction === "rtl" ? 0 : 2,
                  pr: direction === "rtl" ? 2 : 0,
                }}
              >
                {[
                  t(
                    "Create a custom website tailored specifically to your requirements and needs."
                  ),
                  t(
                    "Focus exclusively on delivering a high-quality, bug-free solution within a short development period."
                  ),
                  t(
                    "Incorporate features and functionalities that enhance user experience and operational efficiency."
                  ),
                  t(
                    "Provide maintenance and support for two years post-deployment, ensuring the software operates smoothly and any potential issues are resolved promptly."
                  ),
                ].map((item, index) => (
                  <ListItem
                    key={index}
                    disableGutters
                    sx={{
                      py: { xs: 1.5, md: 2 },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                        transform:
                          direction === "rtl"
                            ? "translateX(-10px)"
                            : "translateX(10px)",
                      },
                      display: "flex",
                      alignItems: "center",
                      flexDirection:
                        direction === "rtl" ? "row-reverse" : "row",
                    }}
                  >
                    {/* <CheckCircle
                      sx={{
                        mr: direction === "rtl" ? 0 : { xs: 1.5, md: 2 },
                        ml: direction === "rtl" ? { xs: 1.5, md: 2 } : 0,
                        fontSize: { xs: 20, md: 24 },
                        color: "primary.main",
                        flexShrink: 0,
                      }}
                    /> */}
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: { xs: "0.9rem", md: "1.1rem" },
                          color: "text.secondary",
                          textAlign: textAlign,
                          flexGrow: 1,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </section>

        {/* Contact Us Section */}
        <section
          id="ContactUs"
          style={{ padding: "40px 0", direction, textAlign }}
        >
          <Container
            maxWidth="lg"
            sx={{ direction: direction, textAlign: textAlign }}
          >
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 6 },
                borderRadius: 2,
                background: "#f5f5f5",
                color: "#333",
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                fontWeight={600}
                align="center"
                sx={{
                  mb: 4,
                  fontSize: { xs: "1.5rem", md: "2.5rem" },
                  color: "#1976d2",
                }}
              >
                {t("Get in Touch")}
              </Typography>

              <Box
                sx={{
                  maxWidth: 600,
                  mx: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    color: "#666",
                    fontSize: { xs: "1rem", md: "1.3rem" },
                  }}
                >
                  {t(
                    "Ready to transform your business? Let's discuss how we can help you achieve your goals."
                  )}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: { xs: 3, md: 4 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, md: 2 },
                      p: { xs: 2, md: 3 },
                      borderRadius: 2,
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      },
                      flexDirection:
                        direction === "rtl" ? "row-reverse" : "row",
                    }}
                  >
                    <PhoneAndroid
                      sx={{ fontSize: { xs: 24, md: 32 }, color: "#1976d2" }}
                    />
                    <Box
                      sx={{
                        textAlign: direction === "rtl" ? "right" : "left",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#666",
                          fontSize: { xs: "0.8rem", md: "0.875rem" },
                        }}
                      >
                        {t("Call Us")}
                      </Typography>
                      <a
                        href={`tel:${t("call")}`}
                        style={{ textDecoration: "none" }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            fontSize: { xs: "1.1rem", md: "1.25rem" },
                            "&:hover": {
                              color: "#1976d2", // Optional: change color on hover
                              textDecoration: "underline", // Optional: add underline on hover
                            },
                          }}
                        >
                          {t("call")}
                        </Typography>
                      </a>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Container>
        </section>

        <Language />
      </Container>
    </>
  );
};
export default Home;
